import { useEffect, useRef, useState } from 'react';
import { getAuthHeaders } from './util';
import { useTranslation } from './hooks/useTranslation';
import { useButtons, useAuth, isTelegram, isStandalone } from './platform';
import { Smartphone, LogOut, Settings as SettingsIcon, Check } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui';
import { cn } from './lib/utils';

function Settings(props) {
  const { t, changeLanguage } = useTranslation();
  const { hideAllButtons } = useButtons();
  const { isAuthenticated, logout } = useAuth();
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [languages, setLanguages] = useState([]);
  const [selectedLang, setSelectedLang] = useState(null);
  const [canAddToHomeScreen, setCanAddToHomeScreen] = useState(isTelegram);
  const onBackClickRef = useRef(null);

  onBackClickRef.current = () => {
    props.setAppLocation('#/home');
  };

  function fetchSettings(setLanguages, setSelectedLang, setLoading, setError, props) {
    setLoading(true);
    fetch('/api/settings', {
      headers: getAuthHeaders(props.chat)
    })
      .then(res => {
        if (res.ok) {
          return res.json();
        }
        return Promise.reject(res);
      })
      .then(response => {
        setLanguages(response.result.languages || []);
        setSelectedLang(response.result.languages.find(lang => lang.selected)?.id || null);
        setLoading(false);
      })
      .catch(reason => {
        setLoading(false);
        console.error('Failed to load settings', reason);
        setError(reason);
      });
  }

  const addToHomeScreen = () => {
    if (canAddToHomeScreen && isTelegram) {
      window.Telegram.WebApp.addToHomeScreen();
      setTimeout(() => {
        setCanAddToHomeScreen(false);
      }, 1000);
    }
  }

  useEffect(() => {
    hideAllButtons();
    fetchSettings(setLanguages, setSelectedLang, setLoading, setError, props);

    // Check home screen status only in Telegram mode
    if (isTelegram) {
      window.Telegram.WebApp.checkHomeScreenStatus((status) => {
        if (status === 'unknown' || status === 'missed') {
          setCanAddToHomeScreen(true);
        }
      });
    }
  }, [hideAllButtons]);

  const handleLanguageChange = async (langId) => {
    // Map language ID to language code (matches backend ChosenLang enum in lang.go)
    // LangEnglish = 1, LangGerman = 2
    const langIdToCodeMap = {
      1: 'en',
      2: 'de'
    };

    const langCode = langIdToCodeMap[langId] || 'en';

    try {
      await changeLanguage(langCode);
      setSelectedLang(langId);
      fetchSettings(setLanguages, setSelectedLang, setLoading, setError, props);
    } catch (error) {
      console.error('Failed to update language', error);
      setError(error);
    }
  };

  if (error) {
    return <div className="page-error">Error: {error.message || t('error')}</div>;
  }

  if (loading) {
    return <div className="page-progress-wrap">
      <div className="page-progress"></div>
    </div>;
  }

  return (
    <div className="page-wrap pb-6">
      {/* Settings Header */}
      <div className="flex flex-col items-center pt-8 pb-6 text-center">
        <div className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-primary/15 to-primary/5 rounded-2xl mb-4">
          <SettingsIcon size={40} className="text-primary" />
        </div>
        <h1 className="text-2xl font-bold">{t('settings') || 'Settings'}</h1>
      </div>

      <div className="space-y-4">
        {/* Add to Home Screen */}
        {canAddToHomeScreen && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">{t('add_home_screen') || 'Add to Home Screen'}</CardTitle>
              <CardDescription>
                {t('miniapp_description') || 'Access the app quickly from your home screen.'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <button
                onClick={addToHomeScreen}
                className="flex items-center gap-3 w-full p-3 rounded-lg bg-secondary hover:bg-secondary/80 transition-colors"
              >
                <Smartphone size={20} className="text-primary" />
                <span className="font-medium">{t('add_home_screen') || 'Add to Home Screen'}</span>
              </button>
            </CardContent>
          </Card>
        )}

        {/* Language Selection */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">{t('language') || 'Language'}</CardTitle>
            <CardDescription>
              {t('language_notifications_description') || 'This affects bot messages and notifications.'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-1">
              {languages.map(lang => (
                <button
                  key={lang.id}
                  onClick={() => handleLanguageChange(lang.id)}
                  className={cn(
                    "flex items-center justify-between w-full p-3 rounded-lg transition-colors",
                    selectedLang === lang.id
                      ? "bg-primary/10 text-primary"
                      : "hover:bg-secondary"
                  )}
                >
                  <span className="font-medium">{lang.name}</span>
                  {selectedLang === lang.id && (
                    <Check size={18} className="text-primary" />
                  )}
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Logout (Standalone mode only) */}
        {isStandalone && isAuthenticated && (
          <Card>
            <CardContent className="pt-6">
              <button
                onClick={() => {
                  logout();
                  props.setAppLocation('/home');
                }}
                className="flex items-center gap-3 w-full p-3 rounded-lg text-destructive hover:bg-destructive/10 transition-colors"
              >
                <LogOut size={20} />
                <span className="font-medium">{t('head_logout') || 'Log out'}</span>
              </button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

export default Settings;
